/**
 * Alert Engine - Monitors well data and generates smart alerts
 */

export interface Alert {
  id: string;
  wellId: string;
  wellName: string;
  type: 'pressure-drop' | 'tank-level' | 'tank-full' | 'maintenance-due' | 'production-drop' | 'water-cut';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  data?: any;
  acknowledged: boolean;
  actionRequired: boolean;
}

export interface AlertRule {
  id: string;
  type: string;
  enabled: boolean;
  threshold?: number;
  parameters?: any;
}

class AlertEngine {
  private alerts: Alert[] = [];
  private rules: AlertRule[] = [
    { id: 'pressure-drop', type: 'pressure-drop', enabled: true, threshold: 10 },
    { id: 'tank-level', type: 'tank-level', enabled: true, threshold: 85 },
    { id: 'tank-full', type: 'tank-full', enabled: true },
    { id: 'maintenance-due', type: 'maintenance-due', enabled: true },
    { id: 'production-drop', type: 'production-drop', enabled: true, threshold: 20 },
    { id: 'water-cut', type: 'water-cut', enabled: true, threshold: 50 },
  ];
  private listeners: Set<(alerts: Alert[]) => void> = new Set();

  constructor() {
    this.loadAlertsFromStorage();
  }

  // Subscribe to alert changes
  public subscribe(callback: (alerts: Alert[]) => void): () => void {
    this.listeners.add(callback);
    callback(this.alerts);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.alerts));
  }

  // Analyze well and generate alerts
  public analyzeWell(well: any, previousData?: any): Alert[] {
    const newAlerts: Alert[] = [];

    // Check pressure drop
    if (this.isRuleEnabled('pressure-drop') && previousData?.dropPressure && well.dropPressure) {
      const dropPercent = ((previousData.dropPressure - well.dropPressure) / previousData.dropPressure) * 100;
      const threshold = this.getThreshold('pressure-drop');
      
      if (dropPercent > threshold) {
        newAlerts.push({
          id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          wellId: well.id,
          wellName: well.name,
          type: 'pressure-drop',
          severity: dropPercent > 20 ? 'critical' : 'warning',
          title: '⚠️ Pressure Drop Alert',
          message: `Drop pressure decreased by ${dropPercent.toFixed(1)}% (from ${previousData.dropPressure} to ${well.dropPressure} PSI)`,
          timestamp: new Date().toISOString(),
          data: { previous: previousData.dropPressure, current: well.dropPressure, dropPercent },
          acknowledged: false,
          actionRequired: dropPercent > 20,
        });
      }
    }

    // Check tank level
    if (this.isRuleEnabled('tank-level') && well.oilTanks && well.oilTanks.length > 0) {
      well.oilTanks.forEach((tank: any, index: number) => {
        const level = tank.currentLevel || 0;
        const capacity = tank.capacity || 0;
        const threshold = this.getThreshold('tank-level');
        
        if (capacity > 0) {
          const fillPercent = (level / capacity) * 100;
          
          if (fillPercent > threshold) {
            newAlerts.push({
              id: `alert-${Date.now()}-tank-${index}-${Math.random().toString(36).substr(2, 9)}`,
              wellId: well.id,
              wellName: well.name,
              type: 'tank-level',
              severity: fillPercent > 95 ? 'critical' : 'warning',
              title: '🛢️ Tank Level Alert',
              message: `Tank ${index + 1} is ${fillPercent.toFixed(1)}% full (${level.toFixed(1)} BBL). Schedule pickup soon.`,
              timestamp: new Date().toISOString(),
              data: { tankIndex: index, level, capacity, fillPercent },
              acknowledged: false,
              actionRequired: fillPercent > 90,
            });
          }
        }
      });
    }

    // Estimate tank full date
    if (this.isRuleEnabled('tank-full') && well.oilTanks && well.oilProduction > 0) {
      well.oilTanks.forEach((tank: any, index: number) => {
        const level = tank.currentLevel || 0;
        const capacity = tank.capacity || 0;
        const remaining = capacity - level;
        const dailyProduction = well.oilProduction || 0;
        
        if (remaining > 0 && dailyProduction > 0) {
          const daysUntilFull = remaining / dailyProduction;
          
          if (daysUntilFull <= 7 && daysUntilFull > 0) {
            const fullDate = new Date();
            fullDate.setDate(fullDate.getDate() + Math.floor(daysUntilFull));
            
            newAlerts.push({
              id: `alert-${Date.now()}-full-${index}-${Math.random().toString(36).substr(2, 9)}`,
              wellId: well.id,
              wellName: well.name,
              type: 'tank-full',
              severity: daysUntilFull <= 2 ? 'critical' : daysUntilFull <= 4 ? 'warning' : 'info',
              title: '📅 Tank Full Estimate',
              message: `Tank ${index + 1} estimated full by ${fullDate.toLocaleDateString()} (${Math.floor(daysUntilFull)} days). Current: ${level.toFixed(1)} BBL, Production: ${dailyProduction.toFixed(1)} BPD`,
              timestamp: new Date().toISOString(),
              data: { tankIndex: index, daysUntilFull, fullDate: fullDate.toISOString(), level, capacity, dailyProduction },
              acknowledged: false,
              actionRequired: daysUntilFull <= 3,
            });
          }
        }
      });
    }

    // Check maintenance due
    if (this.isRuleEnabled('maintenance-due') && well.lastMaintenance) {
      const daysSinceMaintenance = (Date.now() - new Date(well.lastMaintenance).getTime()) / (1000 * 60 * 60 * 24);
      const maintenanceInterval = well.maintenanceInterval || 30; // Default 30 days
      
      if (daysSinceMaintenance >= maintenanceInterval) {
        newAlerts.push({
          id: `alert-${Date.now()}-maint-${Math.random().toString(36).substr(2, 9)}`,
          wellId: well.id,
          wellName: well.name,
          type: 'maintenance-due',
          severity: daysSinceMaintenance > maintenanceInterval + 7 ? 'critical' : 'warning',
          title: '🔧 Maintenance Due',
          message: `Maintenance overdue by ${Math.floor(daysSinceMaintenance - maintenanceInterval)} days. Last service: ${new Date(well.lastMaintenance).toLocaleDateString()}`,
          timestamp: new Date().toISOString(),
          data: { daysSinceMaintenance, maintenanceInterval },
          acknowledged: false,
          actionRequired: true,
        });
      }
    }

    // Check production drop
    if (this.isRuleEnabled('production-drop') && previousData?.oilProduction && well.oilProduction) {
      const dropPercent = ((previousData.oilProduction - well.oilProduction) / previousData.oilProduction) * 100;
      const threshold = this.getThreshold('production-drop');
      
      if (dropPercent > threshold) {
        newAlerts.push({
          id: `alert-${Date.now()}-prod-${Math.random().toString(36).substr(2, 9)}`,
          wellId: well.id,
          wellName: well.name,
          type: 'production-drop',
          severity: dropPercent > 30 ? 'critical' : 'warning',
          title: '📉 Production Drop',
          message: `Oil production decreased by ${dropPercent.toFixed(1)}% (from ${previousData.oilProduction.toFixed(1)} to ${well.oilProduction.toFixed(1)} BPD)`,
          timestamp: new Date().toISOString(),
          data: { previous: previousData.oilProduction, current: well.oilProduction, dropPercent },
          acknowledged: false,
          actionRequired: dropPercent > 30,
        });
      }
    }

    // Check water cut
    if (this.isRuleEnabled('water-cut')) {
      const totalProduction = (well.oilProduction || 0) + (well.waterProduction || 0);
      if (totalProduction > 0) {
        const waterCut = ((well.waterProduction || 0) / totalProduction) * 100;
        const threshold = this.getThreshold('water-cut');
        
        if (waterCut > threshold) {
          newAlerts.push({
            id: `alert-${Date.now()}-wc-${Math.random().toString(36).substr(2, 9)}`,
            wellId: well.id,
            wellName: well.name,
            type: 'water-cut',
            severity: waterCut > 70 ? 'critical' : waterCut > 60 ? 'warning' : 'info',
            title: '💧 High Water Cut',
            message: `Water cut is ${waterCut.toFixed(1)}%. Consider water management strategies.`,
            timestamp: new Date().toISOString(),
            data: { waterCut, oilProduction: well.oilProduction, waterProduction: well.waterProduction },
            acknowledged: false,
            actionRequired: waterCut > 70,
          });
        }
      }
    }

    // Add new alerts to the list
    if (newAlerts.length > 0) {
      this.alerts = [...newAlerts, ...this.alerts].slice(0, 100); // Keep last 100 alerts
      this.saveAlertsToStorage();
      this.notifyListeners();
    }

    return newAlerts;
  }

  // Analyze all wells
  public analyzeAllWells(wells: any[], previousWellsData?: any[]): Alert[] {
    const allNewAlerts: Alert[] = [];
    
    wells.forEach(well => {
      const previousData = previousWellsData?.find(w => w.id === well.id);
      const alerts = this.analyzeWell(well, previousData);
      allNewAlerts.push(...alerts);
    });

    return allNewAlerts;
  }

  // Get all alerts
  public getAlerts(): Alert[] {
    return this.alerts;
  }

  // Get unacknowledged alerts
  public getUnacknowledgedAlerts(): Alert[] {
    return this.alerts.filter(a => !a.acknowledged);
  }

  // Get alerts for a specific well
  public getAlertsForWell(wellId: string): Alert[] {
    return this.alerts.filter(a => a.wellId === wellId);
  }

  // Get critical alerts
  public getCriticalAlerts(): Alert[] {
    return this.alerts.filter(a => a.severity === 'critical' && !a.acknowledged);
  }

  // Acknowledge alert
  public acknowledgeAlert(alertId: string): void {
    const alert = this.alerts.find(a => a.id === alertId);
    if (alert) {
      alert.acknowledged = true;
      this.saveAlertsToStorage();
      this.notifyListeners();
    }
  }

  // Acknowledge all alerts
  public acknowledgeAllAlerts(): void {
    this.alerts.forEach(alert => alert.acknowledged = true);
    this.saveAlertsToStorage();
    this.notifyListeners();
  }

  // Clear old alerts (older than 30 days)
  public clearOldAlerts(): void {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    this.alerts = this.alerts.filter(alert => 
      new Date(alert.timestamp) > thirtyDaysAgo || !alert.acknowledged
    );
    
    this.saveAlertsToStorage();
    this.notifyListeners();
  }

  // Rule management
  private isRuleEnabled(ruleType: string): boolean {
    const rule = this.rules.find(r => r.type === ruleType);
    return rule ? rule.enabled : false;
  }

  private getThreshold(ruleType: string): number {
    const rule = this.rules.find(r => r.type === ruleType);
    return rule?.threshold || 0;
  }

  public updateRule(ruleId: string, updates: Partial<AlertRule>): void {
    const rule = this.rules.find(r => r.id === ruleId);
    if (rule) {
      Object.assign(rule, updates);
      this.saveRulesToStorage();
    }
  }

  public getRules(): AlertRule[] {
    return this.rules;
  }

  // Storage
  private saveAlertsToStorage(): void {
    try {
      localStorage.setItem('oil-well-alerts', JSON.stringify(this.alerts));
    } catch (error) {
      console.error('Failed to save alerts:', error);
    }
  }

  private loadAlertsFromStorage(): void {
    try {
      const stored = localStorage.getItem('oil-well-alerts');
      if (stored) {
        this.alerts = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  }

  private saveRulesToStorage(): void {
    try {
      localStorage.setItem('oil-well-alert-rules', JSON.stringify(this.rules));
    } catch (error) {
      console.error('Failed to save rules:', error);
    }
  }
}

export const alertEngine = new AlertEngine();
